/**
 * useTeamLogStream — single source of truth for the live `team_log_messages`
 * stream.
 *
 * Subscribes via `subscribeWithReconnect` (already handles visibilitychange
 * + exponential backoff) over a `query(orderBy('timestamp','desc'),
 * limit(200))`. Backed by a `Map<id, TeamLogMessage>` so optimistic inserts
 * from the composer are upserts and snapshot deliveries replace them
 * deterministically.
 *
 * Re-subscribes on Firebase auth state changes (token refresh) so a
 * 30-min-background tab restore re-attaches with a fresh ID token rather
 * than failing permission-denied on the cached one.
 *
 * Per S2-4 spec #1129, B-CHANGE-3: not virtualized — relies on the 200
 * message cap and a keyed React list. Swap in `react-window` if message
 * volume per page grows materially.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../lib/firebase";
import { subscribeWithReconnect } from "../lib/firestoreReconnect";
import { getCachedTeamLog, setCachedTeamLog } from "../lib/appShellCache";
import type { TeamLogMessage } from "../types";

const COLLECTION = "team_log_messages";
const PAGE_SIZE = 200;

export interface UseTeamLogStream {
  messages: TeamLogMessage[];
  loaded: boolean;
  /** True while `messages` reflects the IndexedDB cache and the live
   * Firestore snapshot hasn't arrived yet — callers can show a subtle
   * "updating" indicator. */
  loadedFromCache: boolean;
  error: string | null;
  optimisticInsert: (msg: TeamLogMessage) => void;
  optimisticRollback: (id: string) => void;
}

function compareMessages(a: TeamLogMessage, b: TeamLogMessage): number {
  // Numbered messages first by sequence ascending; unnumbered (sequence falsy) last by timestamp.
  if (a.sequence && b.sequence) return a.sequence - b.sequence;
  if (a.sequence) return -1;
  if (b.sequence) return 1;
  return new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();
}

/**
 * Keep the PAGE_SIZE newest numbered posts plus every unnumbered entry
 * (optimistic inserts still waiting for their real post). Input is
 * sorted ascending by `compareMessages`.
 */
function trimToWindow(sorted: TeamLogMessage[]): TeamLogMessage[] {
  const numbered = sorted.filter((m) => m.sequence);
  const pending = sorted.filter((m) => !m.sequence);
  return [...numbered.slice(Math.max(0, numbered.length - PAGE_SIZE)), ...pending];
}

/**
 * Lowest sequence already on screen, where "Load earlier" must page from.
 * Earlier pages win; otherwise the live window. Returns null while the store
 * is only cache-seeded — a cached post is not an anchor, since paging from it
 * skips everything between the stale cache and the live window.
 */
export function oldestLoadedSequence(
  earlier: TeamLogMessage[],
  live: TeamLogMessage[],
  loadedFromCache: boolean,
): number | null {
  const fromEarlier = earlier[0]?.sequence;
  if (fromEarlier != null) return fromEarlier;
  if (loadedFromCache) return null;
  return live.find((m) => m.sequence != null)?.sequence ?? null;
}

export function useTeamLogStream(workspaceId: string): UseTeamLogStream {
  const storeRef = useRef<Map<string, TeamLogMessage>>(new Map());
  // Ids seeded from the IndexedDB cache that no live snapshot has confirmed.
  const seededIdsRef = useRef<Set<string>>(new Set());
  const liveRef = useRef(false);
  const [messages, setMessages] = useState<TeamLogMessage[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [loadedFromCache, setLoadedFromCache] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Render instantly from the last-known IndexedDB snapshot while the live
  // Firestore listener (below) connects in the background. Skipped if the
  // live listener already delivered a snapshot first (fast network / warm
  // connection) so it never clobbers fresher data with a stale cache.
  useEffect(() => {
    let cancelled = false;
    getCachedTeamLog(workspaceId).then((cached) => {
      if (cancelled || !cached || liveRef.current || storeRef.current.size > 0) return;
      for (const m of cached.messages) {
        storeRef.current.set(m.id, m);
        seededIdsRef.current.add(m.id);
      }
      setMessages(Array.from(storeRef.current.values()).sort(compareMessages));
      setLoaded(true);
      setLoadedFromCache(true);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);
  // Bumped when auth changes so the listener resubscribes with a fresh
  // ID token. Without this, a 30-min-background tab can come back and
  // hit Firestore with a stale token that's about to refresh, causing
  // a transient permission-denied before recovering.
  const [authTick, setAuthTick] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, () => {
      setAuthTick((n) => n + 1);
    });
    return unsub;
  }, []);

  useEffect(() => {
    const q = query(collection(db, COLLECTION), where("workspace_id", "==", workspaceId), orderBy("sequence", "desc"), limit(PAGE_SIZE));
    const unsub = subscribeWithReconnect(({ retry, markConnected }) =>
      onSnapshot(
        q,
        (snap) => {
          markConnected();
          // A prior transient failure (stale ID token, brief network blip)
          // sets `error` below and `subscribeWithReconnect`'s retry() often
          // self-heals the underlying listener without this hook ever
          // re-rendering the error away — confirmed live (2026-08-28): the
          // banner stayed stuck reading "Missing or insufficient
          // permissions." indefinitely while messages kept loading
          // correctly beneath it. A later successful snapshot proves the
          // listener is healthy again, so clear it here.
          setError(null);
          // Apply changes incrementally so optimistic entries are not wiped
          // when a re-emission of the same window arrives. `removed` doc
          // changes (window-shift past PAGE_SIZE) drop their entry.
          const store = storeRef.current;
          // Firestore never sends `removed` for cached posts the listener
          // never had, so drop everything the cache seeded; the snapshot
          // re-adds whatever is still in the live window.
          if (seededIdsRef.current.size > 0) {
            for (const id of seededIdsRef.current) store.delete(id);
            seededIdsRef.current.clear();
          }
          liveRef.current = true;
          for (const change of snap.docChanges()) {
            const data = change.doc.data() as Omit<TeamLogMessage, "id">;
            if (change.type === "removed") {
              store.delete(change.doc.id);
            } else {
              const raw = change.doc.data();
            const ts = raw.timestamp
              ? (typeof raw.timestamp === 'string' ? raw.timestamp : raw.timestamp?.toDate?.()?.toISOString?.() ?? new Date().toISOString())
              : (raw.created_at?.toDate?.()?.toISOString?.() ?? new Date().toISOString());
            store.set(change.doc.id, { id: change.doc.id, ...data, timestamp: ts });
            }
          }
          // Keep the newest PAGE_SIZE numbered posts plus pending optimistic
          // entries; only that live window is cached.
          const trimmed = trimToWindow(Array.from(store.values()).sort(compareMessages));
          store.clear();
          for (const m of trimmed) store.set(m.id, m);
          setMessages(trimmed);
          setLoaded(true);
          setLoadedFromCache(false);
          void setCachedTeamLog(workspaceId, trimmed.filter((m) => m.sequence));
        },
        (err) => {
          console.error("[useTeamLogStream]", err);
          setError(err.message || "Failed to load team log");
          setLoaded(true);
          retry();
        },
      ),
    );
    return unsub;
    // authTick deliberately included so resubscribe runs on token refresh.
  }, [authTick, workspaceId]);

  const optimisticInsert = useCallback((msg: TeamLogMessage) => {
    storeRef.current.set(msg.id, msg);
    setMessages(Array.from(storeRef.current.values()).sort(compareMessages));
  }, []);

  const optimisticRollback = useCallback((id: string) => {
    if (storeRef.current.delete(id)) {
      setMessages(Array.from(storeRef.current.values()).sort(compareMessages));
    }
  }, []);

  return { messages, loaded, loadedFromCache, error, optimisticInsert, optimisticRollback };
}

export const __TEST_ONLY__ = { compareMessages, trimToWindow };
